'use client';

import {
  BusinessType,
  IBusinessTypeData,
} from "@order-up/models";
import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { DocumentWithId } from "../interfaces/FirestoreRepository";
import {
  businessTypeService,
  type ISeedBusinessTypesResult,
} from "../services/businessTypes";
import { createLogger } from "../utils/logger";

const logger = createLogger("useBusinessTypes");

const businessTypeKeys = {
  all: ["businessTypes"] as const,
  lists: () => [...businessTypeKeys.all, "list"] as const,
};

/**
 * Hook to fetch platform-wide business types from the business_types collection.
 * Used by admin (manage) and management (read-only reference) apps.
 */
const useGetBusinessTypes = (): UseQueryResult<
  DocumentWithId<BusinessType>[],
  Error
> => {
  return useQuery({
    queryKey: businessTypeKeys.lists(),
    queryFn: () => businessTypeService.getBusinessTypes(),
  });
};

type CreateBusinessTypeInput = Omit<
  IBusinessTypeData,
  "businessTypeId" | "guid" | "createdAt" | "updatedAt"
> & { name: string };

/**
 * Hook to create a business type (admin only).
 */
const useCreateBusinessType = (): UseMutationResult<
  DocumentWithId<BusinessType>,
  Error,
  CreateBusinessTypeInput
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBusinessTypeInput) =>
      businessTypeService.createBusinessType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessTypeKeys.lists(),
      });
    },
  });
};

/**
 * Hook to update a business type by guid (admin only).
 *
 * Keyed on `guid` (a unique UUID) rather than the numeric `businessTypeId`,
 * which is not guaranteed unique across counter resets/re-seeds and could
 * otherwise resolve to the wrong document.
 */
const useUpdateBusinessType = (): UseMutationResult<
  DocumentWithId<BusinessType>,
  Error,
  {
    guid: string;
    data: Partial<
      Omit<IBusinessTypeData, "businessTypeId" | "guid" | "createdAt">
    >;
  }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      guid,
      data,
    }: {
      guid: string;
      data: Partial<
        Omit<IBusinessTypeData, "businessTypeId" | "guid" | "createdAt">
      >;
    }) =>
      businessTypeService.updateBusinessType(guid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessTypeKeys.lists(),
      });
    },
  });
};

/**
 * Hook to seed the curated platform business types & sub-categories.
 *
 * Admin-only. Calls `POST /api/v1/business-types/seed`; the curated payload
 * lives server-side in `@order-up/core`. Idempotent — existing rows are
 * skipped. Invalidates both business-type and business-sub-category caches.
 */
const useSeedBusinessTypes = (): UseMutationResult<
  ISeedBusinessTypesResult,
  Error,
  void
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => businessTypeService.seedBusinessTypes(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: businessTypeKeys.all });
      queryClient.invalidateQueries({ queryKey: ["businessSubCategories"] });
    },
  });
};

/**
 * Hook to delete a business type by guid (admin only).
 */
const useDeleteBusinessType = (): UseMutationResult<
  void,
  Error,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (guid: string) =>
      businessTypeService.deleteBusinessType(guid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessTypeKeys.lists(),
      });
    },
  });
};

/**
 * Subscription hook that listens to business_types collection in real-time.
 * Pushes updates directly into the React Query cache.
 * Use once at the component level where real-time updates are needed.
 */
const useBusinessTypesSubscription = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = businessTypeService.subscribeToBusinessTypes(
      (types) => {
        queryClient.setQueryData(businessTypeKeys.lists(), types);
      },
      (error) => {
        logger.warn("Business types subscription error, falling back to query", { error: error.message });
      },
    );

    return unsubscribe;
  }, [queryClient]);
};

export {
  useGetBusinessTypes,
  useCreateBusinessType,
  useUpdateBusinessType,
  useDeleteBusinessType,
  useSeedBusinessTypes,
  useBusinessTypesSubscription,
  businessTypeKeys,
};
